import React, { useEffect } from 'react';
import { Box, Flex, Text, Badge, Heading, SimpleGrid, Tooltip, VStack, Divider } from '@chakra-ui/react';
import { CricketScore } from '../../types/types';
import { debugCricketScores } from '../../utils/debug';
import { formatTimeWithSecondsInTz, tzShortLabel } from '../../utils/timezone';
import { useTimezone } from '../../contexts/TimezoneContext';

/**
 * Format timestamp to show both UTC and the selected display timezone (time only).
 * UTC is always kept; the second line follows the operator's chosen zone.
 * @param timestamp Unix timestamp in seconds
 * @param timeZone IANA zone ('' = browser local)
 * @returns Formatted string with UTC and selected-zone time (hh:mm:ss)
 */
const formatTimestamp = (timestamp: number, timeZone: string): string => {
  if (!timestamp) return 'N/A';
  // Handle Unix timestamp in seconds (convert to milliseconds)
  const date = new Date(timestamp * 1000);
  const utcTime = date.toISOString().substring(11, 19);
  const zoneTime = formatTimeWithSecondsInTz(date, timeZone);
  const zoneLabel = timeZone ? tzShortLabel(timeZone) : 'Local';
  return `UTC: ${utcTime}\n${zoneLabel}: ${zoneTime}`;
};

interface CricketScoreSequenceProps {
  cricketScores: CricketScore[];
  maxBalls?: number; // Now optional and unused
}

/**
 * Displays a sequence of cricket scores representing current balls
 */
export const CricketScoreSequence: React.FC<CricketScoreSequenceProps> = ({ 
  cricketScores,
  maxBalls // Kept for backward compatibility but not used
}) => {
  const [timeZone] = useTimezone();
  // Debug cricket scores
  useEffect(() => {
    debugCricketScores(cricketScores, 'CricketScoreSequence');
  }, [cricketScores]);

  // If no cricket scores are available, don't render anything
  if (!cricketScores || cricketScores.length === 0) {
    console.log('CricketScoreSequence: No cricket scores to display');
    return null;
  }

  // Parse innings from ballReference and group scores by innings 1 and 2 only
  const groupByInnings = () => {
    const innings: Record<string, CricketScore[]> = {
      '1': [],
      '2': []
    };
    
    // Filter and group only innings 1 and 2
    cricketScores.forEach(score => {
      // Extract innings from ballReference (format usually: "Innings.Over.Ball")
      const inningMatch = score.ballReference.match(/^(\d+)\./); 
      const inning = inningMatch ? inningMatch[1] : '';
      
      // Only process innings 1 and 2
      if (inning === '1' || inning === '2') {
        innings[inning].push(score);
      }
    });
    
    // Sort each innings by sequenceId (ascending for chronological order within innings)
    Object.keys(innings).forEach(inning => {
      innings[inning].sort((a, b) => a.sequenceId - b.sequenceId);
    });
    
    return innings;
  };
  
  const inningsData = groupByInnings();

  return (
    <Box>
      <Heading size="xs" mb={1.5}>Balls by Innings</Heading>
      
      {/* Innings 1 */}
      {inningsData['1'] && inningsData['1'].length > 0 && (
        <VStack align="start" spacing={1} mb={2}>
          <Flex align="center" width="100%">
            <Badge colorScheme="blue" mr={2}>Innings 1</Badge>
            <Text fontSize="sm" fontWeight="medium">
              {inningsData['1'].length} balls ({inningsData['1'].reduce((sum, score) => sum + score.runs, 0)} runs)
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 5, md: 10 }} spacing={1} width="100%" maxH="150px" overflowY="auto">
            {inningsData['1'].map((score) => (
              <Tooltip 
                key={score.sequenceId} 
                label={`${formatTimestamp(score.timestamp, timeZone)}${score.isWicket ? '\nWicket' : ''}`}
              >
                <Box 
                  p={1} 
                  borderWidth="1px" 
                  borderRadius="sm" 
                  bg={score.isWicket ? 'red.100' : (score.runs > 0 ? 'green.100' : 'gray.100')}
                  textAlign="center"
                  position="relative"
                >
                  <Text fontWeight="bold" fontSize="sm">
                    {score.isWicket ? 'W' : (score.runs > 0 ? score.runs : '·')}
                  </Text>
                  <Text fontSize="2xs">{score.ballReference.split('.').slice(1).join('.')}</Text>
                  {score.runs >= 4 && (
                    <Badge 
                      position="absolute" 
                      top="-1" 
                      right="-1" 
                      colorScheme={score.runs === 6 ? 'purple' : 'blue'} 
                      borderRadius="full"
                      size="sm"
                    >
                      {score.runs}
                    </Badge>
                  )}
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
        </VStack>
      )}
      
      {/* Divider between innings */}
      {inningsData['1'] && inningsData['1'].length > 0 && 
       inningsData['2'] && inningsData['2'].length > 0 && (
        <Divider my={1} />
      )}
      
      {/* Innings 2 */}
      {inningsData['2'] && inningsData['2'].length > 0 && (
        <VStack align="start" spacing={1}>
          <Flex align="center" width="100%">
            <Badge colorScheme="green" mr={2}>Innings 2</Badge>
            <Text fontSize="sm" fontWeight="medium">
              {inningsData['2'].length} balls ({inningsData['2'].reduce((sum, score) => sum + score.runs, 0)} runs)
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 5, md: 10 }} spacing={1} width="100%" maxH="150px" overflowY="auto">
            {inningsData['2'].map((score) => (
              <Tooltip 
                key={score.sequenceId} 
                label={`${formatTimestamp(score.timestamp, timeZone)}${score.isWicket ? '\nWicket' : ''}`}
              >
                <Box 
                  p={1} 
                  borderWidth="1px" 
                  borderRadius="sm" 
                  bg={score.isWicket ? 'red.100' : (score.runs > 0 ? 'green.100' : 'gray.100')}
                  textAlign="center"
                  position="relative"
                >
                  <Text fontWeight="bold" fontSize="sm">
                    {score.isWicket ? 'W' : (score.runs > 0 ? score.runs : '·')}
                  </Text>
                  <Text fontSize="2xs">{score.ballReference.split('.').slice(1).join('.')}</Text>
                  {score.runs >= 4 && (
                    <Badge 
                      position="absolute" 
                      top="-1" 
                      right="-1" 
                      colorScheme={score.runs === 6 ? 'purple' : 'blue'} 
                      borderRadius="full"
                      size="sm"
                    >
                      {score.runs}
                    </Badge>
                  )}
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
        </VStack>
      )}
    </Box>
  );
};

export default CricketScoreSequence;
